/**
 * axios 埋点薄壳 —— **这是唯一被 src/utils/http.ts 静态 import 的埋点文件**。
 *
 * 为什么要单独拆一个壳：http.ts 是主 bundle 的一部分，如果它直接 import
 * collectors/apiTiming.ts，就会把 core/config/queue/scheduler/transport 整条
 * 依赖链全部拉进 vendor chunk，懒加载设计彻底失效、首屏体积白涨。
 *
 * 所以这里只做两件极轻的事：
 *   ① 在 config 上打时间戳
 *   ② 把结果交给一个可为空的 recorder
 * recorder 由 SDK 在 idle 时段安装完成后才注入（见 index.ts）。
 * 未注入时整个 hook 是纯 no-op，零开销。
 *
 * 依赖：只有 axios 的**类型**（编译期擦除）。运行时零依赖。
 */
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";

const T0_KEY = "__ckT0";

export interface ApiCallInfo {
  url: string;
  baseURL: string;
  method: string;
  status: number;
  durMs: number;
  /** 401 自动刷新后的重试请求 */
  retry: boolean;
  timeout: boolean;
  resBytes: number | null;
}

type Recorder = (info: ApiCallInfo) => void;

let recorder: Recorder | null = null;

export function setApiRecorder(fn: Recorder | null): void {
  recorder = fn;
}

function nowMs(): number {
  try {
    return typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();
  } catch {
    return Date.now();
  }
}

function emit(
  config: (InternalAxiosRequestConfig & Record<string, unknown>) | undefined,
  status: number,
  headers?: unknown,
  err?: AxiosError,
): void {
  if (!recorder) return;
  try {
    const t0 = config?.[T0_KEY];
    let resBytes: number | null = null;
    const len = (headers as Record<string, unknown> | undefined)?.["content-length"];
    const n = Number(len);
    if (Number.isFinite(n)) resBytes = n;
    recorder({
      url: String(config?.url ?? ""),
      baseURL: String(config?.baseURL ?? ""),
      method: String(config?.method ?? "get").toUpperCase(),
      status,
      durMs: Math.round(nowMs() - (typeof t0 === "number" ? t0 : nowMs())),
      retry: Boolean(config?._retry),
      timeout: err?.code === "ECONNABORTED",
      resBytes,
    });
  } catch {
    /* 采集绝不能影响请求链路 */
  }
}

/**
 * 🔴 必须在 axios.create() 之后、业务自己的 .use() **之前**调用。
 *
 * src/utils/http.ts 的响应拦截器成功分支 `return response.data`（把 AxiosResponse
 * 吃掉了），失败分支 `return Promise.reject(error.response || error.message)`
 * （reject 的不是 AxiosError）。axios 响应拦截器是 FIFO，晚注册就会拿到被改造过
 * 的结果，读 .config 得到 undefined 并抛 TypeError，被业务当成 API 失败处理
 * → 弹错误提示 / 白屏。
 *
 * 同理，本拦截器成功分支必须原样 `return res`、失败分支必须
 * `Promise.reject(err)` **同一个对象**——否则 401 重试链里的
 * `error.config._retry` 判断会失效，用户会被踢到登录页。
 */
export function attachApiTracking(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (res: AxiosResponse) => {
      emit(
        res?.config as InternalAxiosRequestConfig & Record<string, unknown>,
        res?.status ?? 0,
        res?.headers,
      );
      return res;
    },
    (err: AxiosError) => {
      emit(
        err?.config as (InternalAxiosRequestConfig & Record<string, unknown>) | undefined,
        err?.response?.status ?? 0,
        err?.response?.headers,
        err,
      );
      return Promise.reject(err);
    },
  );

  // 请求拦截器 LIFO：先注册 → 最后执行 → t0 不含 accessToken 刷新的等待时间
  instance.interceptors.request.use((config) => {
    try {
      (config as InternalAxiosRequestConfig & Record<string, unknown>)[T0_KEY] = nowMs();
    } catch {
      /* ignore */
    }
    return config;
  });
}
